import { useEffect, useRef, useState } from 'react';

/**
 * useWebSocket — connects to the notifications WebSocket endpoint with
 * auto-reconnect (exponential backoff, capped at 30s). Returns the
 * connection state + the most recent parsed message.
 *
 * The hook reads `token`, `company_id`, `user_id` from localStorage (populated
 * by AuthContext on login). If any of the three is missing it silently becomes
 * a no-op — callers fall back to their existing polling.
 *
 * Options:
 *   onMessage:    (msg) => void — fired for every WS frame that matches filterEvents
 *   filterEvents: string[] | null — if set, only frames whose `event` field is
 *                 in the list are forwarded to onMessage
 *
 * Returns: { connected, lastMessage, reconnectCount }
 */
export default function useWebSocket({ onMessage, filterEvents = null } = {}) {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [reconnectCount, setReconnectCount] = useState(0);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const closedByUnmountRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  const filterEventsRef = useRef(filterEvents);

  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { filterEventsRef.current = filterEvents; }, [filterEvents]);

  useEffect(() => {
    closedByUnmountRef.current = false;

    const connect = () => {
      const token = localStorage.getItem('token') || '';
      const companyId = localStorage.getItem('company_id') || '';
      const userId = localStorage.getItem('user_id') || '';

      if (!token || !companyId || !userId) {
        // No credentials yet — stay silent so the fallback polling keeps working.
        return;
      }

      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const url = `${proto}//${host}/api/notifications/ws/${encodeURIComponent(companyId)}/${encodeURIComponent(userId)}?token=${encodeURIComponent(token)}`;

      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        setReconnectCount(0);
        setConnected(true);
      };

      ws.onmessage = (ev) => {
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        const filter = filterEventsRef.current;
        if (filter && !filter.includes(msg.event)) return;
        setLastMessage(msg);
        if (onMessageRef.current) {
          try { onMessageRef.current(msg); } catch { /* swallow caller errors */ }
        }
      };

      ws.onerror = () => {
        setConnected(false);
      };

      ws.onclose = () => {
        setConnected(false);
        if (!closedByUnmountRef.current) {
          scheduleReconnect();
        }
      };
    };

    const scheduleReconnect = () => {
      const attempt = reconnectAttemptsRef.current;
      const delay = Math.min(30000, 1000 * Math.pow(2, attempt));
      reconnectAttemptsRef.current = attempt + 1;
      setReconnectCount(attempt + 1);
      reconnectTimerRef.current = setTimeout(() => {
        if (!closedByUnmountRef.current) connect();
      }, delay);
    };

    connect();

    return () => {
      closedByUnmountRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { connected, lastMessage, reconnectCount };
}
